<template>
  <div>
    <BittsLoading :is-loading="loading || !ready">
      <div class="login-options-card">
        <BittsCard title-text="Login Options">
          <BillingCTA
            v-if="!isSupernodeOrEnterpriseTier && !isEarlyAdopter"
            :billing-interaction="{
              cta: ORGANIZATION_SETTINGS_CTAS.SSO,
              talkToSalesReason: 'Enabling SSO',
              event_site: EVENT_SITES.LOGIN_CARD_SSO_CTA,
            }"
            data-testid="sso-cta"
            description="Enforce how your team logs in and allow users to quickly log in from your SSO provider on our Supernode plan"
            button-text="Talk to Sales"
            learn-more-external-link="https://help.crossbeam.com/en/articles/4477745-saml-sso"
            message="Make logging in simple & secure with SSO"
            size="large"
            class="m-16"
          >
            <template #image>
              <img src="@/assets/pngs/ctas/sso.png" alt="" />
            </template>
          </BillingCTA>
          <div v-else class="login-options-card__content">
            <!-- SSO Section -->
            <div class="flex">
              <h2 class="login-options-card__section-title">
                Single Sign-On (SSO)
              </h2>
              <div class="login-options-card__section-content">
                <div
                  :class="{
                    'border border-neutral-border rounded-md p-6':
                      isSsoConfigured,
                  }"
                >
                  <div class="login-options-card__switch-item">
                    <div class="login-options-card__switch-content">
                      <div class="flex items-center gap-8">
                        <div class="font-bold"> SSO Configuration </div>
                        <BittsTag :color="isSsoSetup ? 'success' : 'neutral'">
                          {{ ssoStatus }}
                        </BittsTag>
                      </div>
                      <div class="text-neutral-text">
                        Allow authentication via SAML SSO with an identity
                        provider.
                      </div>
                    </div>
                    <BittsButton
                      v-if="!isSsoConfigured"
                      text="Configure SAML SSO"
                      size="small"
                      variant="outline"
                      :left-icon="['fak', 'settings']"
                      @click="ssoConfigFormRef?.handleOpenSsoConfigModal?.()"
                    />
                    <BittsSwitch
                      v-else
                      v-model="ssoEnabled"
                      :disabled="!isSsoConfigured"
                      data-testid="sso-checked-switch"
                      @change="handleSsoToggle"
                    />
                  </div>
                  <div
                    v-if="isSsoConfigured && ssoConfigFormRef?.configSummary"
                    class="flex justify-between items-center mt-6 rounded-md bg-neutral-background-weak p-16"
                  >
                    <div class="font-bold truncate mr-8">
                      {{ ssoConfigFormRef?.configSummary }}
                    </div>
                    <BittsButton
                      size="small"
                      variant="outline"
                      class="bg-neutral-background-weak"
                      data-testid="edit-certificate-button"
                      :center-icon="['fak', 'edit']"
                      aria-label="Edit SSO configuration"
                      @click="ssoConfigFormRef?.handleOpenSsoConfigModal?.()"
                    />
                  </div>
                </div>
                <SSOConfigForm
                  v-if="ssoConfigData"
                  ref="ssoConfigFormRef"
                  :sso-enabled="ssoEnabled"
                  :sso-config-data="ssoConfigData"
                  @update:state="handleSsoFormStateUpdate"
                />
              </div>
            </div>

            <template v-if="isScimAvailable">
              <BittsDivider class="my-20" />

              <!-- SCIM Section -->
              <div class="flex">
                <h2 class="login-options-card__section-title">
                  SCIM Provisioning
                </h2>
                <div class="login-options-card__section-content">
                  <BittsAlert
                    v-if="isScimDisabled"
                    color="warning"
                    message="Configure SSO to use SCIM. SCIM provisioning is only available when SAML SSO is setup and enabled."
                    class="w-full"
                  />
                  <div
                    :class="{
                      'border border-neutral-border rounded-md p-6':
                        ssoConfigData?.obfuscated_token,
                    }"
                  >
                    <div class="login-options-card__switch-item">
                      <div
                        class="login-options-card__switch-content"
                        :class="{
                          'text-neutral-text-placeholder': isScimDisabled,
                        }"
                      >
                        <div class="flex items-center gap-8">
                          <div class="font-bold"> SCIM Configuration </div>
                          <BittsTag
                            :color="
                              ssoConfigData?.obfuscated_token
                                ? 'success'
                                : 'neutral'
                            "
                          >
                            {{ scimStatus }}
                          </BittsTag>
                        </div>
                        <div>
                          Automatically provision and deprovision users through
                          your Identity Provider using SCIM.
                        </div>
                      </div>

                      <BittsButton
                        v-if="!ssoConfigData?.obfuscated_token"
                        text="Configure SCIM"
                        size="small"
                        variant="outline"
                        :disabled="isScimDisabled"
                        :left-icon="['fak', 'settings']"
                        @click="handleOpenScimConfig"
                      />
                      <BittsSwitch
                        v-else
                        :model-value="isScimEnabled"
                        :disabled="isScimDisabled"
                        data-testid="scim-checked-switch"
                        @change="handleScimToggle"
                      />
                    </div>
                    <div
                      v-if="
                        ssoConfigData?.obfuscated_token &&
                        ssoConfigData?.scim_url &&
                        scimConfigFormRef?.obfuscatedToken
                      "
                      class="px-12 py-16"
                    >
                      <BittsInput
                        :model-value="ssoConfigData.scim_url"
                        :form-label="{
                          title: 'SCIM Base URL',
                          helpText:
                            'Use this URL when configuring your Identity Provider.',
                        }"
                        :read-only="true"
                        :allow-copy="true"
                        name="scim-base-url"
                      >
                        <template #prefix>
                          <div class="flex items-center mr-8">
                            <FontAwesomeIcon
                              :icon="['fak', 'globe']"
                              class="h-16 w-16 text-neutral-text-button"
                            />
                          </div>
                        </template>
                      </BittsInput>
                    </div>
                    <div
                      v-if="
                        ssoConfigData?.obfuscated_token &&
                        scimConfigFormRef?.obfuscatedToken
                      "
                      class="flex justify-between items-center mt-6 rounded-md bg-neutral-background-weak p-16"
                    >
                      <div class="flex flex-col">
                        <div class="font-bold">
                          Token {{ scimConfigFormRef?.obfuscatedToken }}
                        </div>
                        <div
                          v-if="scimConfigFormRef?.tokenGeneratedTime"
                          class="text-neutral-text text-[12px]"
                        >
                          Generated {{ scimConfigFormRef?.tokenGeneratedTime }}
                        </div>
                      </div>
                      <BittsButton
                        size="small"
                        variant="outline"
                        type="danger"
                        text="Revoke token"
                        data-testid="revoke-token-button"
                        class="bg-neutral-background-weak"
                        @click="scimConfigFormRef?.handleRevokeToken?.()"
                      />
                    </div>
                  </div>
                  <SCIMConfigForm
                    v-if="showScimForm"
                    ref="scimConfigFormRef"
                    :sso-config-data="ssoConfigData"
                    :sso-enabled="ssoEnabled"
                    @refresh-config="loadSsoSettings"
                  />
                  <div
                    v-if="ssoConfigData?.obfuscated_token"
                    class="flex justify-end"
                  >
                    <BittsButton
                      text="Configure Crossbeam roles in your IDP"
                      size="small"
                      variant="outline"
                      :left-icon="['fak', 'settings']"
                      @click="showScimRolesModal = true"
                    />
                  </div>
                </div>
                <SCIMRolesModal
                  v-if="showScimRolesModal"
                  @closed="showScimRolesModal = false"
                />
              </div>
            </template>

            <BittsDivider class="my-20" />

            <BittsButton
              class="self-end"
              size="small"
              text="Save Settings"
              data-testid="save-settings-button"
              :disabled="!isSsoConfigured || !hasUnsavedChanges"
              @click="confirmSaveSettings"
            />

            <BillingCTA
              v-if="isEarlyAdopter"
              cta-type="sso"
              description="As an early adopter, you already have access to this feature. Continue to support us while unlocking even more."
              message="You've been given a gift"
              :billing-interaction="{
                isEarlyAdopter: true,
                cta: ORGANIZATION_SETTINGS_CTAS.LOGIN_OPTIONS_EA,
                event_site: EVENT_SITES.LOGIN_CARD_EA_CALLOUT,
                talkToSalesReason:
                  'An early adopter who already has SSO unlocked',
              }"
              class="mt-16 mb-0"
            />
          </div>
        </BittsCard>
      </div>
    </BittsLoading>
    <BittsModal
      v-if="showDisableSsoConfirmation"
      variant="confirm"
      :title="confirmTitle"
      :description="confirmMessage"
      primary-button-text="Confirm"
      @action="confirmDisableSso"
      @closed="handleCancelDisableSso"
    >
      <template #image>
        <FontAwesomeIcon
          :icon="['fak', 'warning']"
          style="height: 48px; width: 48px; color: currentColor"
          class="text-warning-accent mb-16"
        />
      </template>
    </BittsModal>
    <BittsModal
      v-if="showSsoConfirmationModal"
      title="Are you sure you want to require SSO?"
      primary-button-text="Yes, Require SSO"
      @action="handleSaveSettings"
      @closed="handleCloseConfirmationModal"
    >
      <BittsAlert
        color="warning"
        message="This will deactivate some users accounts"
        description="Any non-SSO user not defined as an SSO exception will be deleted and forced to sign in as a new user with SSO. This means:"
      >
        <template #bitts-alert-extra-content>
          <ul class="list-disc text-neutral-600 ml-12 pl-0 leading-relaxed">
            <li>
              No users will be "converted" into SSO users. Their current user
              will be deleted and their SSO login will create a new view-only
              user in Crossbeam.
            </li>
            <li>Any user that signed up via SSO will not be deleted.</li>
            <li>
              Any user listed as an SSO exception will not be deleted. If these
              users log in via SSO, they will have two Crossbeam accounts with
              different login methods.
            </li>
          </ul>
        </template>
      </BittsAlert>
    </BittsModal>
  </div>
</template>

<script setup lang="ts">
import {
  BittsAlert,
  BittsButton,
  BittsCard,
  BittsDivider,
  BittsInput,
  BittsLoading,
  BittsModal,
  BittsSwitch,
  BittsTag,
} from '@crossbeam/bitts';
import { EVENT_SITES, ORGANIZATION_SETTINGS_CTAS } from '@crossbeam/itly';

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { storeToRefs } from 'pinia';
import { computed, nextTick, onMounted, ref, watch } from 'vue';

import BillingCTA from '@/components/billing/BillingCTA.vue';

import SSOConfigForm from '@/pages/organization/LoginOptions/components/SSOConfigForm.vue';

import { crossbeamApi } from '@/api';
import useAuth from '@/composables/useAuth';
import {
  NEW_SSO_MODAL,
  SCIM_FOR_SUPERNODE,
  SSO,
} from '@/constants/feature_flags';
import { NO_SSO, SSO_ONLY, SSO_OPTIONAL } from '@/constants/me';
import { captureException } from '@/errors';
import {
  allReady,
  useBillingStore,
  useFeatureFlagStore,
  useFlashesStore,
  useRolesStore,
  useTeamStore,
} from '@/stores';
import { Role } from '@/types/roles';
import { ScimProvisioning, SsoConfigSetup } from '@/types/sso_config';

import SCIMConfigForm from './SCIMConfigForm.vue';
import SCIMRolesModal from './SCIMRolesModal.vue';

type SSOConfigFormExposed = {
  handleOpenSsoConfigModal: () => void;
  getFormData: () => {
    signOnUrl: string;
    certificate: string;
    samlMetadataUrl: string;
    pickedSSOCoreRole: string | null;
    pickedSSOSalesRole: string | null;
    ssoOnlyToggle: boolean;
    ssoExceptions: number[];
    ssoRoles: Role[];
  };
  certificatePreview: string;
  configSummary: string;
};

type SCIMConfigFormExposed = {
  handleOpenScimConfigModal: (currentFormData?: {
    signOnUrl?: string;
    certificate?: string;
    samlMetadataUrl?: string;
    pickedSSOCoreRole?: string | null;
    pickedSSOSalesRole?: string | null;
  }) => Promise<void>;
  scimEnforcement: boolean;
  obfuscatedToken: string | null;
  tokenGeneratedTime: string | null;
  handleRevokeToken: () => Promise<void>;
};

const ssoConfigFormRef = ref<SSOConfigFormExposed | null>(null);
const scimConfigFormRef = ref<SCIMConfigFormExposed | null>(null);

const { currentOrg } = useAuth();

const billingStore = useBillingStore();
const featureFlagStore = useFeatureFlagStore();
const rolesStore = useRolesStore();
const teamStore = useTeamStore();
const flashesStore = useFlashesStore();

const { isSupernodeOrEnterpriseTier, isEnterpriseTier } =
  storeToRefs(billingStore);
const ready = allReady(rolesStore, teamStore);

const hasNewSsoModalFlag = computed(() =>
  featureFlagStore.hasFeatureFlag(NEW_SSO_MODAL),
);

const isScimAvailable = computed(
  () =>
    isEnterpriseTier.value ||
    featureFlagStore.hasFeatureFlag(SCIM_FOR_SUPERNODE),
);

const loading = ref(true);
const showDisableSsoConfirmation = ref(false);
const showSsoConfirmationModal = ref(false);
const showScimRolesModal = ref(false);

// ssoEnabled as a ref that can be toggled, initialized from currentOrg
const ssoEnabled = ref(currentOrg.value.login_method !== 'default');

const ssoFormState = ref<{
  anyFieldsPending: boolean;
  ssoOnlyToggle: boolean;
  metadataResaveRequested: boolean;
} | null>(null);

const ssoConfigData = ref<SsoConfigSetup | null>(null);

// Store initial state for comparison
const savedSsoEnabled = ref<boolean>(false);
const savedSsoFormData = ref<{
  login_url: string;
  certificate: string;
  saml_metadata_url: string;
  default_role: string | null;
  default_sales_edge_role: string | null;
  ssoOnlyToggle: boolean;
} | null>(null);
const savedScimProvisioning = ref<ScimProvisioning | null>(null);

function handleSsoFormStateUpdate(state: {
  anyFieldsPending: boolean;
  ssoOnlyToggle: boolean;
  metadataResaveRequested: boolean;
}) {
  ssoFormState.value = state;
}

const isSsoConfigured = computed(() => {
  return ssoFormState.value?.anyFieldsPending === false;
});

const isSsoSetup = computed(() => {
  return !!savedSsoFormData.value?.login_url;
});

const isScimDisabled = computed(() => {
  return !savedSsoEnabled.value || !ssoEnabled.value;
});

const showScimForm = computed(() => {
  return (
    ssoConfigData.value &&
    (ssoEnabled.value || ssoConfigData.value?.obfuscated_token)
  );
});

const isScimEnabled = computed(() => {
  if (!isScimAvailable.value) return false;
  return (
    ssoEnabled.value &&
    (ssoConfigData.value?.scim_provisioning === 'enabled' ||
      ssoConfigData.value?.scim_provisioning === 'enforced')
  );
});

const ssoStatus = computed(() => {
  if (!isSsoSetup.value) return 'Not configured';
  return savedSsoFormData.value?.ssoOnlyToggle ? 'Enforced' : 'Configured';
});

const scimStatus = computed(() => {
  if (!ssoConfigData.value?.obfuscated_token) return 'Not configured';
  return savedScimProvisioning.value === 'enforced'
    ? 'Enforced'
    : 'Token generated';
});

const confirmTitle = computed(() => {
  return isScimEnabled.value
    ? 'Disable SSO and SCIM authentication?'
    : 'Disable SSO authentication?';
});

const confirmMessage = computed(() => {
  return isScimEnabled.value
    ? 'Disabling SSO will also disable SCIM provisioning and deprovisioning. Users will no longer be managed through your identity provider.'
    : 'Disabling SSO will prevent users from signing in using Single Sign-On. This change can be reverted at any time.';
});

const isEarlyAdopter = computed(
  () =>
    (ssoEnabled.value || featureFlagStore.hasFeatureFlag(SSO)) &&
    !isSupernodeOrEnterpriseTier.value,
);

const hasUnsavedChanges = computed(() => {
  // If SSO is not configured, no changes can be saved
  if (!isSsoConfigured.value || !savedSsoFormData.value) {
    return false;
  }

  // Check if ssoEnabled changed
  if (ssoEnabled.value !== savedSsoEnabled.value) {
    return true;
  }

  if (ssoFormState.value?.metadataResaveRequested) {
    return true;
  }

  // Get current form data
  const currentFormData = ssoConfigFormRef.value?.getFormData();
  if (!currentFormData) return false;

  // Check SSO form fields
  const currentSsoOnlyToggle = currentFormData.ssoOnlyToggle;
  if (
    currentFormData.signOnUrl !== savedSsoFormData.value.login_url ||
    currentFormData.certificate !== savedSsoFormData.value.certificate ||
    (hasNewSsoModalFlag.value &&
      currentFormData.samlMetadataUrl !==
        savedSsoFormData.value.saml_metadata_url) ||
    currentFormData.pickedSSOCoreRole !== savedSsoFormData.value.default_role ||
    currentFormData.pickedSSOSalesRole !==
      savedSsoFormData.value.default_sales_edge_role ||
    currentSsoOnlyToggle !== savedSsoFormData.value.ssoOnlyToggle
  ) {
    return true;
  }

  if (isScimAvailable.value) {
    if (isScimEnabled.value) {
      const currentScimEnforcement =
        scimConfigFormRef.value?.scimEnforcement ?? false;
      const currentScimProvisioning: ScimProvisioning = currentScimEnforcement
        ? 'enforced'
        : 'enabled';
      if (currentScimProvisioning !== savedScimProvisioning.value) {
        return true;
      }
    } else if (
      savedScimProvisioning.value &&
      savedScimProvisioning.value !== 'disabled'
    ) {
      // SCIM was enabled but is now disabled
      return true;
    }
  }

  return false;
});

async function loadSsoSettings() {
  try {
    const { data: ssoConfigResponse, error: ssoConfigError } =
      await crossbeamApi.GET('/v0.1/sso/config');

    if (ssoConfigError) {
      captureException(ssoConfigError);
      flashesStore.addErrorFlash({
        message: 'There was a problem loading your SSO settings.',
      });
      return;
    }

    // Store SSO config data
    const configData = ssoConfigResponse as SsoConfigSetup;
    ssoConfigData.value = configData;

    // Store initial state for change tracking
    savedSsoEnabled.value = currentOrg.value.login_method !== 'default';
    if (configData) {
      savedSsoFormData.value = {
        login_url: configData.login_url || '',
        certificate: configData.certificate || '',
        // @ts-expect-error saml_metadata_url is not in the OAS yet
        saml_metadata_url: configData.saml_metadata_url ?? '',
        default_role: configData.default_role ?? null,
        default_sales_edge_role: configData.default_sales_edge_role ?? null,
        ssoOnlyToggle: currentOrg.value.login_method === SSO_ONLY,
      };
      savedScimProvisioning.value = configData.scim_provisioning ?? null;
    }
  } catch (err) {
    captureException(err);
    flashesStore.addErrorFlash({
      message: 'There was a problem loading your SSO settings.',
    });
  }
}

async function confirmSaveSettings() {
  if (!ssoConfigFormRef.value) return;

  const formData = ssoConfigFormRef.value.getFormData();
  if (
    ssoEnabled.value &&
    formData.ssoOnlyToggle &&
    !savedSsoFormData.value?.ssoOnlyToggle
  ) {
    showSsoConfirmationModal.value = true;
  } else {
    await handleSaveSettings();
  }
}

function handleCloseConfirmationModal() {
  showSsoConfirmationModal.value = false;
}

async function handleSsoToggle(newValue: boolean) {
  // If turning SSO off, revert and show confirmation
  if (!newValue) {
    // Wait for next tick to ensure the toggle has updated, then revert it
    await nextTick();
    ssoEnabled.value = true;
    showDisableSsoConfirmation.value = true;
  }
}

function confirmDisableSso() {
  ssoEnabled.value = false;
  showDisableSsoConfirmation.value = false;
}

function handleCancelDisableSso() {
  showDisableSsoConfirmation.value = false;
}

function handleScimToggle(newValue: boolean) {
  if (ssoConfigData.value) {
    ssoConfigData.value.scim_provisioning = newValue ? 'enabled' : 'disabled';
  }
}

function handleOpenScimConfig() {
  // Get current form data from SSOConfigForm to use latest values
  const formData = ssoConfigFormRef.value?.getFormData();
  if (formData) {
    scimConfigFormRef.value?.handleOpenScimConfigModal?.({
      signOnUrl: formData.signOnUrl,
      certificate: formData.certificate,
      samlMetadataUrl: formData.samlMetadataUrl,
      pickedSSOCoreRole: formData.pickedSSOCoreRole,
      pickedSSOSalesRole: formData.pickedSSOSalesRole,
    });
  } else {
    // Fallback if form data is not available
    scimConfigFormRef.value?.handleOpenScimConfigModal?.();
  }
}

async function handleSaveSettings() {
  if (!ssoConfigFormRef.value) return;

  const formData = ssoConfigFormRef.value.getFormData();
  const selectedRole = formData.ssoRoles?.find(
    (item) => item.id?.toString() === formData.pickedSSOCoreRole,
  );

  const scimProvisioning: ScimProvisioning =
    !ssoEnabled.value || !isScimEnabled.value
      ? 'disabled'
      : scimConfigFormRef.value?.scimEnforcement
        ? 'enforced'
        : 'enabled';

  const useMetadataUrl = hasNewSsoModalFlag.value && !!formData.samlMetadataUrl;
  const body = {
    ...(useMetadataUrl
      ? { saml_metadata_url: formData.samlMetadataUrl }
      : {
          login_url: formData.signOnUrl,
          certificate: formData.certificate,
        }),
    default_core_role_id: selectedRole?.id?.toString() ?? null,
    default_sales_edge_role: formData.pickedSSOSalesRole ?? null,
    scim_provisioning: scimProvisioning,
  };

  try {
    const { error: ssoConfigError } = await crossbeamApi.PUT(
      '/v0.2/sso/config',
      {
        // @ts-expect-error saml_metadata_url is not in the OAS yet
        body,
      },
    );

    if (ssoConfigError) {
      captureException(ssoConfigError);
      const errorMessage =
        (ssoConfigError as { errors?: string[] }).errors?.join(', ') ||
        'Failed to save SSO settings';
      flashesStore.addErrorFlash({ message: errorMessage });
      return;
    }

    const ssoStatusValue = ssoEnabled.value
      ? formData.ssoOnlyToggle
        ? SSO_ONLY
        : SSO_OPTIONAL
      : NO_SSO;

    const orgLoginMethodPayload = {
      login_method: ssoStatusValue,
      ...(formData.ssoOnlyToggle && { sso_admins: formData.ssoExceptions }),
    };

    const { error: loginMethodError } = await crossbeamApi.PATCH(
      '/v0.1/organization/login-method',
      {
        body: orgLoginMethodPayload,
      },
    );

    if (loginMethodError) {
      captureException(loginMethodError);
      flashesStore.addErrorFlash({
        message: 'Failed to update login method',
      });
      return;
    }

    currentOrg.value.login_method = ssoStatusValue;
    teamStore.refreshTeamStore();
    billingStore.refreshBillingStore();
    flashesStore.addSuccessFlash({ message: 'Changes Saved' });

    handleSsoConfigSaved();
  } finally {
    // Always close the confirmation modal, even if there was an error
    handleCloseConfirmationModal();
  }
}

async function handleSsoConfigSaved() {
  ssoEnabled.value = currentOrg.value.login_method !== 'default';
  await loadSsoSettings();
}

onMounted(async () => {
  await loadSsoSettings();
  loading.value = false;
});

// Watch for changes to currentOrg.login_method to sync the ref
watch(
  () => currentOrg.value.login_method,
  (newMethod) => {
    ssoEnabled.value = newMethod !== 'default';
  },
);
</script>

<style scoped lang="pcss">
@reference "../../../../css/theme.css";
.login-options-card__content {
  @apply flex flex-col pt-0 p-20;
}

.login-options-card__section-title {
  @apply text-[16px] font-bold w-[400px];
}

.login-options-card__section-content {
  @apply flex flex-col gap-24 w-full;
}

.login-options-card__switch-item {
  @apply flex p-12 gap-16 items-center w-full;
}

.login-options-card__switch-content {
  @apply flex flex-col gap-4 w-full;
}
</style>
